import _ from 'lodash';
import moment from 'moment';

import createModel, { prop } from 'models/lib/create_model';
import ConversationItem from './conversation_item';
import ConversationItemType from './conversation_item_type';

const ONGOING_WINDOW_MINUTES = 30;

const VoiceAICall = createModel({
  modelName: 'VoiceAICall',

  properties: {
    automationSessionId: prop(String).isRequired,
    isTranscriptCollapsed: prop(Boolean).isRequired,
    customerNumber: prop(String),
    companyNumber: prop(String),
    transcribedMessages: prop([])
      .fromJs(function (item) {
        return ConversationItem.fromJs(item);
      })
      .default([]),
    recordings: prop([])
      .fromJs(function (item) {
        return ConversationItem.fromJs(item);
      })
      .default([]),
    endedAt: prop(String),
  },

  statics: {
    create(attrs) {
      return new VoiceAICall(attrs);
    },
  },

  setTranscriptCollapsed(isTranscriptCollapsed) {
    this.isTranscriptCollapsed = isTranscriptCollapsed;
  },

  addChildItem(childItem) {
    switch (childItem.contentType()) {
      case ConversationItemType.VOICE_AI_MESSAGE:
        this.addOrUpdateTranscribedMessage(childItem);
        break;
      case ConversationItemType.VOICE_AI_RECORDING:
        this.addRecording(childItem);
        break;
      case ConversationItemType.VOICE_AI_SESSION_ENDED:
        this.endedAt = childItem.content.endedAt;
        break;
      default:
        throw new Error(`unknown voice ai message type: ${childItem.contentType()}`);
    }
  },

  addOrUpdateTranscribedMessage(message) {
    const idx = this.transcribedMessages.findIndex(m => m.id === message.id);
    if (idx < 0) {
      this.transcribedMessages.push(message);
    } else {
      this.transcribedMessages[idx] = message;
    }
  },

  addRecording(recording) {
    const idx = this.recordings.findIndex(m => m.id === recording.id);
    if (idx < 0) {
      this.recordings.push(recording);
    } else {
      this.recordings[idx] = recording;
    }
  },

  getTranscribedMessages() {
    return _.sortBy(this.transcribedMessages, message => moment(message.timestamp).valueOf());
  },

  isCallRecorded() {
    return this.recordings.length > 0;
  },

  getVoiceAIRecording() {
    return this.isCallRecorded() ? this.recordings[0] : undefined;
  },

  getVoiceAIRecordingStatus() {
    return this.isCallRecorded() ? this.recordings[0].content.recordingStatus : undefined;
  },

  getVoiceAIRecordingId() {
    return this.isCallRecorded() ? this.recordings[0].content.recordingId : undefined;
  },

  startedAt() {
    const firstMessage = this.getTranscribedMessages()[0];
    return firstMessage && firstMessage.timestamp;
  },

  callDuration() {
    const startedAt = this.startedAt();
    if (!startedAt) {
      // No speech detected
      return 0;
    }

    if (this.endedAt) {
      return moment(this.endedAt).diff(moment(startedAt), 'seconds');
    }

    // older calls may not have endedAt set, so we calculate duration based on the last message sent
    const lastMessageSentAt = _.last(this.getTranscribedMessages())?.timestamp;
    return moment(lastMessageSentAt).diff(moment(startedAt), 'seconds');
  },

  isOngoingCall() {
    // if the recording item exists the automation has ended
    if (this.isCallRecorded()) {
      return false;
    }

    // when endedAt is undefined, we consider the automation ongoing if it is within the ongoing window (30 minutes)
    // both startedAt and endedAt cannot be undefined at the same time because if there is no transcribed messages and no recording
    // there should be no VoiceAI item
    if (this.endedAt === undefined) {
      return moment().diff(moment(this.startedAt()), 'minutes') < ONGOING_WINDOW_MINUTES;
    }

    return false;
  },
});

export default VoiceAICall;
